import React from 'react';
import { Box, Container, Typography, Paper, Table, TableBody, TableCell, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import SEOHead from './SEO/SEOHead';

const PageHeader = styled(Box)(({ theme }) => ({
    backgroundColor: '#1a2e4a',
    color: '#ffffff',
    padding: theme.spacing(4, 2),
    marginBottom: theme.spacing(4),
}));

const Section = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(5),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: '#1a2e4a',
    borderBottom: `2px solid ${theme.palette.orange?.main || '#e67e22'}`,
    paddingBottom: theme.spacing(1),
    display: 'inline-block',
}));

const InfoTable = styled(Paper)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    overflow: 'hidden',
    '& .MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5, 2),
    },
    '& .MuiTableRow-root:last-child .MuiTableCell-root': {
        borderBottom: 'none',
    },
}));

const LabelCell = styled(TableCell)(({ theme }) => ({
    backgroundColor: '#f8f9fa',
    fontWeight: 600,
    width: '35%',
    color: '#1a2e4a',
}));

const ContactBox = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    backgroundColor: '#f8f9fa',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    textAlign: 'center',
}));

const ResellerProgram: React.FC = () => {
    return (
        <>
            <SEOHead 
                page="reseller-program"
                customMeta={{
                    title: "Reseller Program | Point Sails",
                    description: "Information for boat dealers and rigging services considering the Point Sails reseller program.",
                    keywords: "sail reseller, boat dealer sails, rigging services, wholesale sails"
                }}
            />
            
            {/* Simple Header */}
            <PageHeader>
                <Container maxWidth="md">
                    <Typography variant="h4" sx={{ fontWeight: 500, color: '#ffffff' }}>
                        Reseller Program
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                        Program details for boat dealers and rigging service providers
                    </Typography>
                </Container>
            </PageHeader>

            <Container maxWidth="md" sx={{ pb: 6 }}>
                
                {/* Overview */}
                <Section>
                    <SectionTitle variant="h5">Overview</SectionTitle>
                    <Typography sx={{ lineHeight: 1.7 }}>
                        The reseller program allows boat dealers, brokers, and rigging services to quote and order custom sails 
                        for their customers through Point Sails. You receive wholesale pricing and can keep your margin or pass some or all of it to the customer. 
                        All sails are made to order—no inventory required.
                    </Typography>
                </Section>

                {/* Pricing & Margins */}
                <Section>
                    <SectionTitle variant="h5">Pricing & Margins</SectionTitle>
                    <InfoTable elevation={0}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <LabelCell>Your Discount</LabelCell>
                                    <TableCell>Wholesale pricing below retail. Discount level discussed during onboarding.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Margin Flexibility</LabelCell>
                                    <TableCell>
                                        Keep your full margin, pass some to the customer, or pass all of it through—your choice per order
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Price Display</LabelCell>
                                    <TableCell>
                                        Your cart shows your wholesale price. Print options available for customer-facing quotes at retail or discounted pricing.
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </InfoTable>
                </Section>

                {/* How Orders Work */}
                <Section>
                    <SectionTitle variant="h5">How Orders Work</SectionTitle>
                    <InfoTable elevation={0}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <LabelCell>1. Quote</LabelCell>
                                    <TableCell>
                                        Select boat model from database (5,000+ boats) or enter custom measurements (coming soon). Configure sail options. Price updates instantly.
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>2. Customer Approval</LabelCell>
                                    <TableCell>
                                        Share quote with your customer. Adjust discount if needed. Customer approves.
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>3. Order & Deposit</LabelCell>
                                    <TableCell>
                                        Submit order through checkout. 50% deposit collected at time of order.
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>4. Production</LabelCell>
                                    <TableCell>
                                        Sails are manufactured. Typical production time is 4-6 weeks depending on sail type and current queue.
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>5. Delivery</LabelCell>
                                    <TableCell>
                                        Balance due before shipping. Sails ship directly to you or your customer.
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </InfoTable>
                </Section>

                {/* Payment Terms */}
                <Section>
                    <SectionTitle variant="h5">Payment Terms</SectionTitle>
                    <InfoTable elevation={0}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <LabelCell>Deposit</LabelCell>
                                    <TableCell>50% at time of order</TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Balance</LabelCell>
                                    <TableCell>50% due before shipping</TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Payment Methods</LabelCell>
                                    <TableCell>Credit card, ACH transfer</TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Net Terms</LabelCell>
                                    <TableCell>Not currently available. Extended terms planned for established accounts.</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </InfoTable>
                </Section>

                {/* What You Get */}
                <Section>
                    <SectionTitle variant="h5">What's Included</SectionTitle>
                    <InfoTable elevation={0}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <LabelCell>Reseller Dashboard</LabelCell>
                                    <TableCell>
                                        Track all orders, view order history, manage customer references
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Boat Database</LabelCell>
                                    <TableCell>
                                        Access to 5,000+ boat models with pre-loaded rig dimensions for instant quoting
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Support Materials</LabelCell>
                                    <TableCell>
                                        Measurement forms and rigging diagrams to verify dimensions with your customer
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Support</LabelCell>
                                    <TableCell>
                                        Email support via crew@pointsails.com. Response within 2 business days. Phone/video calls available for complex orders.
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </InfoTable>
                </Section>

                {/* Requirements */}
                <Section>
                    <SectionTitle variant="h5">Requirements</SectionTitle>
                    <InfoTable elevation={0}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <LabelCell>Eligible Businesses</LabelCell>
                                    <TableCell>
                                        Boat dealers, yacht brokers, rigging services, marine service providers, yacht management companies
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Minimum Orders</LabelCell>
                                    <TableCell>None. No volume commitments or quotas.</TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Onboarding</LabelCell>
                                    <TableCell>
                                        Brief orientation call to review platform, pricing tools, and answer questions (~30 minutes)
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </InfoTable>
                </Section>

                {/* How to Get Started */}
                <Section>
                    <SectionTitle variant="h5">Getting Started</SectionTitle>
                    <InfoTable elevation={0}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <LabelCell>Step 1</LabelCell>
                                    <TableCell>
                                        Create an account at pointsails.com
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Step 2</LabelCell>
                                    <TableCell>
                                        Email crew@pointsails.com to request reseller access. Include your business name and type.
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Step 3</LabelCell>
                                    <TableCell>
                                        Complete a brief orientation call
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <LabelCell>Step 4</LabelCell>
                                    <TableCell>
                                        Account upgraded. Reseller pricing and dashboard become visible.
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </InfoTable>
                </Section>

                {/* Contact */}
                <Section>
                    <ContactBox elevation={0}>
                        <Typography variant="h6" sx={{ mb: 1, color: '#1a2e4a' }}>
                            Questions?
                        </Typography>
                        <Typography sx={{ mb: 2, color: 'text.secondary' }}>
                            We're happy to answer any questions before you decide.
                        </Typography>
                        <Box 
                            component="a" 
                            href="mailto:crew@pointsails.com?subject=Reseller%20Program%20Question"
                            sx={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: 1,
                                color: '#1a2e4a',
                                textDecoration: 'none',
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            <EmailIcon sx={{ color: 'orange.main' }} />
                            crew@pointsails.com
                        </Box>
                    </ContactBox>
                </Section>

            </Container>
        </>
    );
};

export default ResellerProgram;
